import React, { useState } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import {
  Box,
  Heading,
  HStack,
  FormControl,
  FormLabel,
  Select,
  Text,
} from "@chakra-ui/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChartBar } from "@fortawesome/free-solid-svg-icons";
import { StandardPageTitle, StandardHeader } from "../../themes/StandardHeader";

const VaccineUsageSatisfactionChart = ({ data, criterionOptions }) => {
  const [selectedCriterion, setSelectedCriterion] = useState("");

  // จัดการการเปลี่ยนแปลงใน dropdown
  const handleCriterionChange = (e) => {
    setSelectedCriterion(e.target.value);
  };

  // กรองเกณฑ์ที่มีข้อมูลใน data
  const availableCriteria = [
    ...new Set(data.map((item) => item.Criterion)),
  ].filter(Boolean);
  const filteredCriterionOptions = criterionOptions.filter((criterion) =>
    availableCriteria.includes(criterion)
  );

  // เตรียมข้อมูลสำหรับกราฟ
  const chartData = () => {
    if (!selectedCriterion) {
      // รวมยอดทุกเกณฑ์ตาม VaccineNameTH
      const groupedData = data.reduce((acc, item) => {
        const vaccineName = item.VaccineNameTH;
        if (!acc[vaccineName]) {
          acc[vaccineName] = {
            name: vaccineName,
            rating: 0,
            count: 0,
            criterion: "ทุกเกณฑ์",
          };
        }
        acc[vaccineName].rating += parseFloat(item.AverageRating);
        acc[vaccineName].count += 1;
        return acc;
      }, {});

      return Object.values(groupedData).map((item) => ({
        name: item.name,
        rating: (item.rating / item.count).toFixed(2),
        criterion: item.criterion,
      }));
    } else {
      // กรองข้อมูลตามเกณฑ์ที่เลือก
      return data
        .filter((item) => item.Criterion === selectedCriterion)
        .map((item) => ({
          name: item.VaccineNameTH,
          rating: item.AverageRating,
          criterion: item.Criterion,
        }));
    }
  };

  // กำหนดสีสำหรับกราฟ
  const barColor = "#16A085"; // Green, matching iGreen.500

  // Tooltip ที่กำหนดเอง
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <Box
          bg="white"
          p={3}
          border="1px solid #ccc"
          borderRadius="md"
          boxShadow="md"
        >
          <p>
            <strong>วัคซีน:</strong> {label}
          </p>
          <p>
            <strong>เกณฑ์:</strong> {data.criterion}
          </p>
          <p>
            <strong>คะแนนเฉลี่ย:</strong> {data.rating}
          </p>
        </Box>
      );
    }
    return null;
  };

  return (
    <Box
      borderWidth={1}
      borderRadius="lg"
      boxShadow="lg"
      p={5}
      mt={6}
      bg="white"
    >
      <StandardHeader
        title="กราฟสรุปผลการใช้วัคซีน"
        bgColor="iGreen.500"
        icon={faChartBar}
      />
      <FormControl m={5} maxW="300px">
        <FormLabel fontSize="xl" color="gray">
          เลือกเกณฑ์
        </FormLabel>
        {filteredCriterionOptions.length > 0 ? (
          <Select
            value={selectedCriterion}
            onChange={handleCriterionChange}
            placeholder="ทุกเกณฑ์"
            colorScheme="iBlue"
          >
            {filteredCriterionOptions.map((criterion) => (
              <option key={criterion} value={criterion}>
                {criterion}
              </option>
            ))}
          </Select>
        ) : (
          <Text color="gray.500">ไม่มีเกณฑ์ที่มีข้อมูล</Text>
        )}
      </FormControl>
      {chartData().length > 0 ? (
        <ResponsiveContainer width="100%" height={500}>
          <BarChart
            data={chartData()}
            margin={{ top: 40, right: 30, left: 20, bottom: 60 }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              dataKey="name"
              angle={-45}
              textAnchor="end"
              interval={0}
              height={150}
              tick={{ fontSize: 12 }}
            />
            <YAxis
              label={{
                value: "คะแนนเฉลี่ย (1-5)",
                angle: -90,
                position: "insideLeft",
                offset: -5,
              }}
              tick={{ fontSize: 12 }}
              domain={[0, 5]}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend
              layout="horizontal"
              verticalAlign="top"
              align="right"
              wrapperStyle={{ paddingBottom: 10 }}
            />
            <Bar
              dataKey="rating"
              name="คะแนนเฉลี่ย"
              fill={barColor}
              barSize={50}
            />
          </BarChart>
        </ResponsiveContainer>
      ) : (
        <Text textAlign="center" color="gray.500" p={5}>
          ไม่มีข้อมูลสำหรับแสดงกราฟ
        </Text>
      )}
    </Box>
  );
};

export default VaccineUsageSatisfactionChart;
